"use client"

import Image from "next/image"
import { ReactNode } from "react"
import { ArrowUpRightMini } from "@medusajs/icons"
import { useFrontendPreferences } from "@lib/frontend-preferences"
import type { HeroBanner } from "@lib/data/hero-banners"
import type { StorefrontSettings } from "@lib/data/storefront-settings"
import AboutEmblem from "@modules/home/components/about-emblem"
import Hero from "@modules/home/components/hero"
import CategoryMediaRail, {
  type CategoryMediaRailItem,
} from "@modules/home/components/category-media-rail"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type HomeContentProps = {
  bestSellers: ReactNode
  heroBanners?: HeroBanner[]
  categoryMediaRailItems?: CategoryMediaRailItem[]
  storefrontSettings?: StorefrontSettings | null
}

export default function HomeContent({
  bestSellers,
  heroBanners,
  categoryMediaRailItems,
  storefrontSettings,
}: HomeContentProps) {
  const { locale, t } = useFrontendPreferences()
  const resortHref = "/collections"
  const resortLabel =
    (locale === "fa"
      ? storefrontSettings?.resort_label_fa
      : storefrontSettings?.resort_label_en) || t.home.resortCollection

  return (
    <>
      <Hero banners={heroBanners} />

      {bestSellers}

      <section
        className="sahil-paired-editorial"
        aria-label={t.home.pairedEditorialAria}
      >
        <LocalizedClientLink
          href={resortHref}
          className="relative sahil-paired-editorial__panel"
        >
          <Image
            src={
              storefrontSettings?.resort_primary_image ||
              "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/uBGdKpFMdLibrLiE.jpg"
            }
            alt={resortLabel}
            fill
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="sahil-paired-editorial__image"
          />
          <span className="sahil-paired-editorial__wash" />
          <span className="sahil-paired-editorial__label">
            {resortLabel}
          </span>
        </LocalizedClientLink>
        <LocalizedClientLink
          href={resortHref}
          className="relative sahil-paired-editorial__panel"
          aria-label={t.home.pairedEditorialAria}
        >
          <Image
            src={
              storefrontSettings?.resort_secondary_image ||
              "https://files.manuscdn.com/user_upload_by_module/session_file/310519663366518963/EKQLZghydDqyiabQ.jpg"
            }
            alt=""
            fill
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="sahil-paired-editorial__image"
          />
        </LocalizedClientLink>
      </section>

      <CategoryMediaRail categories={categoryMediaRailItems} />

      <section className="sahil-reference-newsletter">
        <div className="content-container">
          <p>{t.home.newsletterTag}</p>
          <h2>
            {t.home.newsletterLine}
            <br />
            <em>{t.home.newsletterEmphasis}</em>
          </h2>
          <LocalizedClientLink href="/account" className="sahil-source-link">
            <span>{t.home.newsletterCta}</span>
            <ArrowUpRightMini aria-hidden="true" />
          </LocalizedClientLink>
        </div>
      </section>

      <section className="sahil-reference-about content-container">
        <div className="sahil-reference-about__copy">
          <p>{t.home.aboutTag}</p>
          <h2>{t.home.aboutText}</h2>
        </div>
        <AboutEmblem />
      </section>
    </>
  )
}
